import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import FileUploaderCard from 'Components/Files/FileUploaderCard';
import InlineFileActionCard from 'Components/ComponentLibrary/Components/Uncategorized/InlineFileActionCard';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Image from 'Components/ComponentLibrary/Atoms/Image';
import PageContentContainer from 'Components/ComponentLibrary/Molecules/Page/PageContentContainer';
import PageContentTop from 'Components/ComponentLibrary/Molecules/Page/PageContentTop';
import { useSnackbarContext } from 'Context/SnackbarContext';
import FileUploadDropAreaWithAttachmentList
  from 'Components/ComponentLibrary/Molecules/FileUploadDropAreaWithAttachmentList';


const useStyles = makeStyles(theme => ({
  documentLabel: {
    marginBottom: theme.spacing(1),
  },
  fileUploadContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
}));


export function CIPStatusItemDocument({
  document, isUploaded, onDelete, onAfterUpload,
}) {
  const classes = useStyles();

  const { displayErrorMessage } = useSnackbarContext();

  const [busy, setBusy] = useState(false);
  function handleStartUpload() {
    setBusy(true);
  }
  async function handleAfterUpload() {
    if (onAfterUpload) await onAfterUpload();
    setBusy(false);
  }

  async function handleDelete() {
    if (document.link('self')) {
      setBusy(true);
      await document.link('self').delete();
      if (onDelete) await onDelete();
      setBusy(false);
    }
  }

  const documentTypePrompt = document.selectedOptionPrompt('documentType');
  const documentUploadDate = document.attr('uploadedAt') ? new Date(document.attr('uploadedAt')).toLocaleDateString(
    'en-US',
    {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
  ) : null;
  const documentImageURL = document.relationship('image')?.link('related').url();
  return (isUploaded ? (
    <div>
      <Typography weight="bold" variant="h5" color="primary" className={classes.documentLabel}>{documentTypePrompt}</Typography>
      <InlineFileActionCard
        media={<Image boxSize="xs" src={documentImageURL} alt={documentTypePrompt} />}
        onDelete={handleDelete}
        title={document.attr('name')}
        description={documentUploadDate}
      />
    </div>
  ) : (
    <div className={classes.fileUploadContainer}><FileUploaderCard
      uploadURL={document.relationship('upload').link('related').url()}
      media={<Image boxSize="md" src={documentImageURL} alt={documentTypePrompt} />}
      title={documentTypePrompt}
      onStart={handleStartUpload}
      onCatch={err => {
        displayErrorMessage(err, err.errors);
      }}
      onFinally={handleAfterUpload}
      disableClick={busy}
      LoaderProps={{
        size: 80,
      }}
    />
    </div>
  )
  );
}
CIPStatusItemDocument.propTypes = {
  document: PropTypes.object.isRequired,
  isUploaded: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAfterUpload: PropTypes.func.isRequired,
};

export default function CIPStatusItemDocumentUploadContent() {
  const {
    refreshPageContentCollection,
  } = usePageContentContext();

  const { resource } = useMetaLayoutContext();
  const isUploaded = resource.attr('isUploaded') === 'true';

  const document = resource.attr('documents')?.resources().head();
  if (!document) {
    return (
      <PageContentContainer>
        <PageContentTop
          title={resource.attr('title')}
          subtitle={'There was an issue while fetching some information. Please try again later.'}
        />
      </PageContentContainer>
    );
  }


  const documentTypeNames = resource.attr('documents')?.resources().list().map(d => d.selectedOptionPrompt('documentType'));

  async function handleDelete() {
    await refreshPageContentCollection();
  }

  return (
    <PageContentContainer>
      <PageContentTop
        resource={resource}
        title={resource.attr('title')}
        subtitle={resource.attr('subtitle')}
      />
      <FileUploadDropAreaWithAttachmentList
        trailingTitleText={documentTypeNames.map(name => `${name}`).join(' or ')}
        subtitle={'JPG, PNG, PDF with max file size 50MB'}
        url={!isUploaded ? document.relationship('upload').link('related').url() : undefined}
        onSuccess={refreshPageContentCollection}
        onError={refreshPageContentCollection}
        acceptedFileTypes="image/*,.pdf"
        attachments={isUploaded ? resource.attr('documents')?.resources().list() : undefined}
        onDelete={handleDelete}
        confirmOnDelete
      />
    </PageContentContainer>
  );
}
